import { useEffect, useState } from "react";
import { X, Share, MoreVertical, Plus, Check } from "lucide-react";

/**
 * Cross-platform "Add SCS to your home screen" helper.
 * Detects the user's OS/browser and shows tailored steps.
 * Manager & operative onboarding aid — nothing here talks to the server.
 */
function detectPlatform() {
    if (typeof navigator === "undefined") return "desktop";
    const ua = navigator.userAgent || "";
    const isIPad = /iPad/.test(ua) || (/(Macintosh|Mac OS X)/.test(ua) && "ontouchend" in document);
    if (isIPad || /iPhone|iPod/.test(ua)) return "ios";
    if (/Android/.test(ua)) return "android";
    return "desktop";
}

function isStandalone() {
    if (typeof window === "undefined") return false;
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    // iOS Safari-specific flag
    return !!window.navigator.standalone;
}

export default function InstallHelperDialog({ open, onClose }) {
    const [platform, setPlatform] = useState("desktop");
    const [installed, setInstalled] = useState(false);
    useEffect(() => {
        if (!open) return;
        setPlatform(detectPlatform());
        setInstalled(isStandalone());
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="install-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
                <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 className="font-display text-xl font-semibold">Add SCS to your home screen</h2>
                            <p className="text-sm text-[var(--text-secondary)] mt-1">
                                Open SCS with a single tap — just like a normal app.
                            </p>
                        </div>
                        <button className="p-2 rounded-full hover:bg-[var(--bg-subtle)] transition-colors -mr-2 -mt-2"
                                onClick={onClose} aria-label="Close" data-testid="close-install-btn">
                            <X size={18} />
                        </button>
                    </div>

                    {installed ? (
                        <div className="mt-5 flex items-center gap-2 p-3 rounded-xl bg-[var(--pine-light)] text-[var(--pine)]">
                            <Check size={16} />
                            <span className="text-sm font-medium">You're already using the installed app 🎉</span>
                        </div>
                    ) : (
                        <div className="mt-5">
                            {platform === "ios" && <IosSteps />}
                            {platform === "android" && <AndroidSteps />}
                            {platform === "desktop" && <DesktopSteps />}
                        </div>
                    )}

                    <div className="flex justify-end mt-6">
                        <button className="btn-primary" onClick={onClose}>Got it</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Step({ n, children, icon }) {
    return (
        <div className="flex gap-3">
            <div className="h-6 w-6 rounded-full bg-[var(--pine)] text-white text-xs font-bold flex items-center justify-center shrink-0">
                {n}
            </div>
            <div className="text-sm text-[var(--text-primary)] pt-0.5 flex-1">
                {children}
                {icon && (
                    <span className="ml-2 inline-flex items-center align-middle rounded-md bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[var(--text-secondary)]">
                        {icon}
                    </span>
                )}
            </div>
        </div>
    );
}

function IosSteps() {
    return (
        <div className="space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--terracotta)] font-semibold">iPhone · iPad · Safari</div>
            <Step n={1}>Tap the <b>Share</b> button at the bottom of Safari <span className="inline-flex items-center align-middle rounded-md bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[var(--text-secondary)]"><Share size={14} /></span></Step>
            <Step n={2}>Scroll down and tap <b>Add to Home Screen</b> <span className="inline-flex items-center align-middle rounded-md bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[var(--text-secondary)]"><Plus size={14} /></span></Step>
            <Step n={3}>Tap <b>Add</b> in the top-right corner. SCS will appear on your home screen like a normal app.</Step>
            <p className="text-xs text-[var(--text-muted)] pt-1">
                Note: on iPhone, the Add to Home Screen option only appears in <b>Safari</b>. If you're in Chrome or another browser, tap the address bar, then "Open in Safari".
            </p>
        </div>
    );
}

function AndroidSteps() {
    return (
        <div className="space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--terracotta)] font-semibold">Android · Chrome</div>
            <Step n={1}>Tap the <b>menu</b> button in the top-right of Chrome <span className="inline-flex items-center align-middle rounded-md bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[var(--text-secondary)]"><MoreVertical size={14} /></span></Step>
            <Step n={2}>Tap <b>Install app</b> (or <b>Add to Home screen</b>).</Step>
            <Step n={3}>Tap <b>Install</b> to confirm. SCS will appear alongside your other apps.</Step>
            <p className="text-xs text-[var(--text-muted)] pt-1">
                On Samsung Internet: menu → <b>Add page to</b> → <b>Home screen</b>.
            </p>
        </div>
    );
}

function DesktopSteps() {
    return (
        <div className="space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--terracotta)] font-semibold">Desktop · Chrome / Edge</div>
            <Step n={1}>Look for the small <b>install icon</b> at the right end of the address bar (looks like a monitor with a down arrow).</Step>
            <Step n={2}>Click it, then click <b>Install</b>.</Step>
            <Step n={3}>SCS opens in its own window and pins to your Dock / Taskbar / Start menu.</Step>
            <p className="text-xs text-[var(--text-muted)] pt-1">
                Don't see the icon? Open the browser menu (⋮) → look for <b>Install SCS…</b>.
            </p>
        </div>
    );
}
